/* ============================================================
   Lost Collective · Long-form Article · post-processor (orchestrator)
   File: assets/lc-article-postprocess.js

   Runs once on DOMContentLoaded. Walks #articleBody and turns
   Shopify rich-text markup into LC long-form markup: figure
   wrappers, EXIF panels, tripartite captions, archive credits,
   chapter h2s, frame-range patches.

   Companion modules (loaded as separate defer scripts so each
   piece can be reviewed and replaced in isolation):
     lc-article-toc.js         — desktop sticky TOC rail + mobile nav
     lc-article-extras.js      — contact sheet, read-next badges, pull quotes
     lc-article-lightbox.js    — tap-to-zoom dialog
     lc-scrollytelling.js      — sticky-image scrolly subsystem (dual-mode)
     lc-article-progress.js    — masthead reading-progress bar
     lc-article-share.js       — share triggers (native + clipboard fallback)

   Inter-module contract: window.LCArticle is a tiny shared object
   that exposes onReady(fn) + fireReady(). The orchestrator calls
   fireReady() once the body is fully rebuilt and figures are
   placed; every dependent module registers an onReady callback
   and runs once the DOM is stable.

   Passes (this file):
     0    Read photographs JSON (from #lc-photographs-json)
     1    Walk children, wrap <img> → <figure class="fig {scale}">
          + figcaption + EXIF panel + archive credit; build h2
          chapter headings from h3 source markers
     2    Compute correct § frame ranges
     3    Replace <!-- lc:wall --> comment nodes with wall template
     3.5  Update hero frame counter with computed total
     7    Drop-cap enforcement on pure-text articles
     10   Move article-images figures into body at section anchors
   ============================================================ */

(function () {
  'use strict';

  // ----- Shared coordination ------------------------------------------------
  window.LCArticle = window.LCArticle || (function () {
    var state = { ready: false, queue: [] };
    return {
      onReady: function (fn) { if (state.ready) fn(); else state.queue.push(fn); },
      fireReady: function () {
        if (state.ready) return;
        state.ready = true;
        state.queue.forEach(function (fn) { try { fn(); } catch (_e) {} });
        state.queue.length = 0;
      }
    };
  })();

  function ready(fn) {
    if (document.readyState !== 'loading') return fn();
    document.addEventListener('DOMContentLoaded', fn);
  }

  ready(function () {
    var root = document.getElementById('articleBody');
    if (!root) {
      // No body to rebuild, but still signal so independent modules
      // (lightbox, share) can stop waiting.
      window.LCArticle.fireReady();
      return;
    }
    if (root.getAttribute('data-lc-processed') === '1') {
      window.LCArticle.fireReady();
      return;
    }

    // Safety net: fireReady must always run, even if a Pass below throws.
    // Without this, dependent modules (scrolly, toc, lightbox) sit forever
    // in the LCArticle.onReady queue and the page renders half-built.
    // Belt-and-braces 5s timeout in case the postprocess hangs entirely.
    var firedReady = false;
    function safeFireReady() {
      if (firedReady) return;
      firedReady = true;
      try { window.LCArticle.fireReady(); } catch (_e) { /* swallow */ }
    }
    setTimeout(safeFireReady, 5000);

    try {

    // Scrollytelling pages: collapse the rail-width grid columns so the
    // sticky TOC rail gap doesn't offset content. data-rail="off" activates
    // the no-rail grid-template-columns variant in lc-article.css.
    var hasScrolly = !!document.querySelector('.lc-scrolly');
    // Server-decided (main-article.liquid), so it holds before
    // lc-scrollytelling.js has built anything.
    var isScrollyArticle = !!root.closest('article.is-scrolly');
    if (hasScrolly) {
      var articleEl = root.closest('article') || root.parentElement;
      if (articleEl) articleEl.setAttribute('data-rail', 'off');
    }

    // Image credits master switch (main-article-images render_credit_blocks),
    // emitted as data-render-credits on the .lc-article-images section. Gates
    // the inline figcaption credit below; the scrolly overlay reads the same
    // flag in lc-scrollytelling.js. ADR-0008 / Blocker B2.
    var creditSectionEl = document.querySelector('.lc-article-images');
    var creditsEnabled = !!(creditSectionEl &&
      creditSectionEl.getAttribute('data-render-credits') === 'true');

    // ---------- Pass 0: read photograph data ----------
    var photographs = {};
    try {
      var jsonEl = document.getElementById('lc-photographs-json');
      if (jsonEl) photographs = JSON.parse(jsonEl.textContent || '{}');
    } catch (_e) { /* graceful degradation */ }
    // Stash on the shared object so extras (contact sheet) can read it.
    window.LCArticle.photographs = photographs;

    // ----- Helpers ----------------------------------------------------------
    function el(tag, attrs, html) {
      var n = document.createElement(tag);
      if (attrs) Object.keys(attrs).forEach(function (k) {
        if (k === 'class') n.className = attrs[k];
        else if (k === 'html') n.innerHTML = attrs[k];
        else n.setAttribute(k, attrs[k]);
      });
      if (html != null) n.innerHTML = html;
      return n;
    }

    function pad(n) { return n < 10 ? '0' + n : '' + n; }

    function esc(s) {
      return String(s || '').replace(/[&<>"']/g, function (c) {
        return {'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c];
      });
    }

    function fmtDate(iso) {
      try {
        var d = new Date(iso);
        return d.toLocaleDateString('en-AU', { year: 'numeric', month: 'short', day: 'numeric' });
      } catch (_e) { return iso; }
    }

    // ----- Pass 1: snapshot and rebuild ------------------------------------
    var KEEP_CLASSES = ['colophon', 'prints-strip', 'photo-index', 'read-next', 'back-link',
                        'lc-prints', 'lc-next', 'lc-colophon', 'lc-contact', 'lc-archive'];
    function isKeeper(node) {
      if (node.nodeType !== 1) return false;
      for (var i = 0; i < KEEP_CLASSES.length; i++) {
        if (node.classList && node.classList.contains(KEEP_CLASSES[i])) return true;
      }
      return false;
    }

    var children = [];
    var keepers = [];
    Array.prototype.slice.call(root.childNodes).forEach(function (n) {
      if (isKeeper(n)) keepers.push(n);
      else if (n.nodeType === 1 || n.nodeType === 8 /* Comment */ ||
               (n.nodeType === 3 && n.textContent.trim())) children.push(n);
    });

    while (root.firstChild) root.removeChild(root.firstChild);

    var frameNo = 1;       // hero is frame 01, body images start at 02
    var bodyImgIndex = 0;  // 1-based index into photographs[] JSON
    var sectionNo = 0;
    var sectionFrameStart = 2;
    var pendingProse = [];
    var firstPara = true;
    var nextSource = null; // the source node after the one being processed
    var lastEmittedFigure = null;
    var duoFigures = [];

    function flushProse() {
      if (!pendingProse.length) { lastEmittedFigure = null; return; }
      var block = el('div', { class: 'prose-block' });
      pendingProse.forEach(function (p) { block.appendChild(p); });
      root.appendChild(block);
      pendingProse = [];
      lastEmittedFigure = null;
    }

    function isCaptionParagraph(p) {
      if (!p || p.nodeType !== 1 || (p.tagName || '').toLowerCase() !== 'p') return false;
      if (p.classList && p.classList.contains('caption')) return true;
      return false;
    }

    function attachCaptionToFigure(p, fig) {
      // Direct child only. pair/tri carry a figcaption per item in nested
      // <figure> elements; an unscoped querySelector would find the first of
      // those and silently overwrite item 1's own caption.
      var existing = fig.querySelector(':scope > figcaption .desc');
      if (!existing) return;
      var text = (p.textContent || '').trim();
      if (!text) return;
      existing.textContent = text;
    }

    // Scale from the photograph's DECLARED size: the source <img>'s own
    // width/height, else lco.photographs. Never naturalWidth: it is 0 until
    // the image has loaded and the real width once it is cached, so the same
    // article laid out as column on a cold load and bleed on a warm one
    // (Kinugawa, 28 of 29 frames, its page height moving ~1,900px between
    // loads). An image with no declared size is a column, which is what a
    // cold load always gave it.
    // The ratio always comes from ONE declared pair, never a width from the
    // tag over a height from lco.photographs: that mix is the same class of
    // error (1600 over 1365 reads 1.17, a false column for a 3:2 landscape).
    // A tag declaring only a width keeps that width, with the photograph's
    // ratio if it has one and 3:2 otherwise, as a cold load always gave it.
    function classifyImg(img, ph) {
      var aw = parseInt(img.getAttribute('width') || 0, 10);
      var ah = parseInt(img.getAttribute('height') || 0, 10);
      var pw = ph ? parseInt(ph.width || 0, 10) : 0;
      var phh = ph ? parseInt(ph.height || 0, 10) : 0;
      var w = aw || pw;
      var ratio = (aw && ah) ? aw / ah : ((pw && phh) ? pw / phh : 1.5);
      if (w >= 2000 && ratio >= 1.4) return 'bleed';
      if (w >= 1400 && ratio >= 1.2) return 'wide';
      return 'column';
    }

    var ARTICLE_SIZES = {
      bleed:  '100vw',
      // 'plate' is a lone photograph at the same footprint a pair/duo/tri group
      // gets, so it caps where they cap: min(--col-w + 400px, 92vw).
      plate:  '(min-width: 1265px) 1260px, 92vw',
      wide:   '(min-width: 990px) 80vw, 100vw',
      column: '(min-width: 990px) 720px, 100vw',
      // pair/tri sit in a figure capped at min(--col-w + 400px, 92vw), so a
      // column is (figure - gaps) / n, not a share of the viewport. The old
      // 32vw/22vw described neither the pre-2026-09 stacked layout nor the
      // current grid: the browser fetched a 281px file for a 372px slot and
      // upscaled it. Above ~1265px the figure stops growing and the columns
      // cap, so the largest hop is a fixed px, not a vw.
      pair:   '(min-width: 1265px) 580px, (min-width: 750px) 46vw, 92vw',
      tri:    '(min-width: 1265px) 380px, (min-width: 750px) 31vw, 92vw',
    };

    // LOS7-2960. Every figure was offered the same [400, 800, 1200] ladder,
    // but `plate` declares a 1260px slot — 2520 device px on a 2x display —
    // so the biggest photograph on the page was the only one served at under
    // half the pixels it paints. Measured across this template's scales on a
    // 43-figure article at 1600px/DPR2, every other scale lands within 3% of
    // what it needs and only `plate` misses (0.48x), so the ladder grows for
    // `plate` alone and every other scale keeps the widths it has today.
    //
    // The top candidate is the frame's own intrinsic width rather than a
    // round number: Shopify never upscales, so a candidate wider than the
    // source would advertise pixels the response cannot contain, and the
    // browser would pick it and still decode a small file.
    var SRCSET_BASE = [400, 800, 1200];
    var SRCSET_PLATE = [400, 800, 1200, 1600, 2000, 2400, 2800];

    function srcsetWidths(scale, ph) {
      if (scale !== 'plate') return SRCSET_BASE;
      var max = (ph && parseInt(ph.width, 10)) || 0;
      if (!max) return SRCSET_BASE;
      var out = [];
      for (var i = 0; i < SRCSET_PLATE.length; i++) {
        if (SRCSET_PLATE[i] >= max) break;
        out.push(SRCSET_PLATE[i]);
      }
      out.push(max);
      return out;
    }

    function shopifyCdnSrcset(src, widths) {
      if (!src || src.indexOf('cdn.shopify.com') === -1) return '';
      var base = src.replace(/[?&]width=\d+(&|$)/, '$1').replace(/[?&]$/, '');
      var sep = base.indexOf('?') === -1 ? '?' : '&';
      return widths.map(function (w) { return base + sep + 'width=' + w + ' ' + w + 'w'; }).join(', ');
    }

    function buildFigure(scale, contents, caption, isPair) {
      var fig = el('figure', { class: 'fig ' + scale + (scale === 'column' ? ' has-margin-cap' : '') });
      if (isPair) {
        var grid = el('div', { class: 'pair-grid' });
        contents.forEach(function (c) { grid.appendChild(c); });
        fig.appendChild(grid);
      } else {
        contents.forEach(function (c) { fig.appendChild(c); });
      }
      if (caption) fig.appendChild(caption);
      return fig;
    }

    // LOS7-2901. A portrait crop for narrow viewports, authored per frame as
    // `cdn_url_mobile` on lco.photographs. The scrollytelling layer has read
    // this field since LOS7-2794 (--bg-m), but an INLINE figure ignored it:
    // the same 2:1 letterbox that works on a desktop column was served to a
    // 390px phone, where it renders about 40px tall. The <picture> below is
    // the inline equivalent, on the same 749px boundary the scrolly static
    // fallback and lc-journey-map.js already use, so one authored field means
    // one thing everywhere. A frame with no cdn_url_mobile emits no <source>
    // and renders exactly as it did before.
    var MOBILE_CROP_MEDIA = '(max-width: 749px)';

    function imgWrap(img, fno, scale, isLcp, ph) {
      var wrap = el('div', { class: 'img' });
      var fnoEl = el('span', { class: 'fno' }, 'Frame ' + pad(fno));
      var clean = el('img', {});
      var src = img.getAttribute('src') || '';
      var sizes = ARTICLE_SIZES[scale] || ARTICLE_SIZES.column;
      var srcset = shopifyCdnSrcset(src, srcsetWidths(scale, ph));
      var mobileSrc = (ph && ph.cdn_url_mobile) ? String(ph.cdn_url_mobile) : '';

      // Everything that does NOT start a fetch is safe to set here. src and
      // srcset are deliberately left until the element is inside its <picture>
      // (below): assigning either one runs the browser's resource-selection
      // algorithm immediately, even on a detached element, and selection made
      // outside a <picture> can only ever pick the landscape candidate. Moving
      // the element under a <source> afterwards does not re-run it, so the
      // portrait crop was requested by nobody and currentSrc stayed landscape.
      clean.alt = img.getAttribute('alt') || '';
      // Intrinsic dimensions. The source <img> tags in body_html carry no
      // width/height and naturalWidth is 0 until the image loads, which this
      // file already says a few hundred lines down (see orientAt) and then did
      // not act on here: the copy below could only ever copy nothing, so NO
      // inline figure on any article has ever shipped width/height, and the
      // browser had no aspect to reserve a box from. lco.photographs carries
      // width and height on every entry and is the build-time source of truth,
      // so fall back to it. The source img still wins where it has them, which
      // keeps any hand-authored figure behaving as before.
      var declaredW = img.getAttribute('width') || (ph && ph.width) || '';
      var declaredH = img.getAttribute('height') || (ph && ph.height) || '';
      if (declaredW && declaredH) {
        clean.setAttribute('width', String(declaredW));
        clean.setAttribute('height', String(declaredH));
      }
      clean.loading = isLcp ? 'eager' : 'lazy';
      if (isLcp) clean.setAttribute('fetchpriority', 'high');
      // The zoom dialog is named after the photograph. Pair, tri and duo
      // captions carry only "Frame NN", so the title travels on the wrap.
      if (ph && ph.title) wrap.setAttribute('data-title', String(ph.title));
      wrap.appendChild(fnoEl);

      if (mobileSrc) {
        var picture = el('picture', {});
        var source = el('source', {});
        source.setAttribute('media', MOBILE_CROP_MEDIA);
        // The mobile crop only ever paints below 749px, where no scale asks
        // for more than the base ladder can serve; measured separately from
        // the desktop plate defect above and left unchanged.
        var mobileSrcset = shopifyCdnSrcset(mobileSrc, SRCSET_BASE);
        source.setAttribute('srcset', mobileSrcset || mobileSrc);
        if (mobileSrcset) source.setAttribute('sizes', sizes);
        // The portrait crop has a different aspect from the landscape one, and
        // the <img>'s width/height would otherwise reserve the LANDSCAPE box
        // for it: the page would lay out a letterbox slot and then reflow to a
        // tall one on load. width/height on a <source> is how the platform
        // answers that, so an entry that declares the crop's intrinsic size
        // gets a correctly shaped box before the bytes arrive. Optional: an
        // entry without them behaves as it would with no width/height at all.
        if (ph.cdn_url_mobile_width && ph.cdn_url_mobile_height) {
          source.setAttribute('width', String(ph.cdn_url_mobile_width));
          source.setAttribute('height', String(ph.cdn_url_mobile_height));
        }
        picture.appendChild(source);
        picture.appendChild(clean);
        wrap.appendChild(picture);
      } else {
        wrap.appendChild(clean);
      }

      // Now the <img> knows its parent, so selection sees the <source>.
      if (srcset) {
        var base = src.replace(/[?&]width=\d+(&|$)/, '$1').replace(/[?&]$/, '');
        var sep = base.indexOf('?') === -1 ? '?' : '&';
        clean.setAttribute('srcset', srcset);
        clean.setAttribute('sizes', sizes);
        clean.src = base + sep + 'width=800';
      } else {
        clean.src = src;
      }
      return wrap;
    }

    function buildCaption(label, desc, meta) {
      var fc = el('figcaption');
      fc.appendChild(el('span', { class: 'lbl', 'aria-hidden': 'true' }, label));
      fc.appendChild(el('span', { class: 'desc' }, desc || ''));
      fc.appendChild(el('span', { class: 'meta', 'aria-hidden': 'true' }, meta || ''));
      return fc;
    }

    function buildExifPanel(ph) {
      if (!ph || !ph.camera) return null;
      var dl = el('dl', { class: 'exif' });
      function row(label, val) {
        if (!val && val !== 0) return '';
        return '<div><dt>' + esc(label) + '</dt><dd>' + esc(String(val)) + '</dd></div>';
      }
      dl.innerHTML =
        row('Camera',    ph.camera) +
        row('Lens',      ph.lens) +
        row('Focal',     ph.focal_mm ? ph.focal_mm + ' mm' : null) +
        row('Aperture',  ph.aperture) +
        row('Shutter',   ph.shutter) +
        row('ISO',       ph.iso) +
        (ph.captured_at ? row('Captured', fmtDate(ph.captured_at)) : '') +
        (ph.print_url
          ? '<div class="buy"><span>Print · ' + esc(ph.paper || 'Ilford Galerie 310 gsm') + '</span>' +
            '<a href="' + esc(ph.print_url) + '">Buy print \u2192</a></div>'
          : '');
      return dl;
    }

    // Resolve a photograph's inline credit. ADR-0008 reconciles the field-name
    // mismatch: the canonical source is now the per-frame `credit` (string) +
    // optional `credit_url` on lco.photographs. archival_metadata is read as a
    // fallback so the 3 legacy archival entries still render a credit even
    // before their `credit` string is populated. The old `ph.source` path
    // (which never matched the Wangi data) is retired.
    function creditTextFor(ph) {
      if (!ph) return null;
      if (ph.credit) return { text: String(ph.credit), url: ph.credit_url || null };
      var am = ph.archival_metadata;
      if (am && typeof am === 'object') {
        var parts = [];
        if (am.creator) parts.push(am.creator);
        if (am.held_by) parts.push(am.held_by);
        var line = parts.join('. ');
        if (am.rights_statement) line = line ? (line + '. ' + am.rights_statement) : am.rights_statement;
        return line ? { text: line, url: am.archive_url || null } : null;
      }
      return null;
    }

    function processImg(img) {
      flushProse();
      var alt = img.getAttribute('alt') || '';

      if (alt.toLowerCase().indexOf('reference') > -1) {
        var refWrap = el('div', { class: 'img' });
        refWrap.appendChild(el('span', { class: 'fno' }, 'Ref'));
        var refClean = el('img', {});
        refClean.alt = alt;
        if (img.getAttribute('width')) refClean.setAttribute('width', img.getAttribute('width'));
        if (img.getAttribute('height')) refClean.setAttribute('height', img.getAttribute('height'));
        // loading must be set BEFORE src: assigning src starts resource
        // selection immediately, so a lazy attribute added afterwards arrives
        // too late to defer anything. It was set two lines below the src here
        // (LOS7-2943). No reference figure exists on the Ikeshima article, so
        // this is a correctness fix with no measured effect on that page.
        refClean.loading = 'lazy';
        refClean.src = img.getAttribute('src') || '';
        refWrap.appendChild(refClean);
        var refFig = el('figure', { class: 'fig column reference has-margin-cap' });
        refFig.appendChild(refWrap);
        refFig.appendChild(buildCaption('REFERENCE', alt, 'PUBLIC ARCHIVE'));
        root.appendChild(refFig);
        lastEmittedFigure = refFig;
        return;
      }

      var fno = ++frameNo;
      bodyImgIndex++;

      var ph = photographs[String(bodyImgIndex)] || null;
      var scale = (ph && ph.scale) ? ph.scale : classifyImg(img, ph);
      if (scale === 'wall') scale = 'column';

      var wrap = imgWrap(img, fno, scale, bodyImgIndex === 1, ph);
      if (ph && ph.frame_no) wrap.querySelector('.fno').textContent = 'Frame ' + pad(ph.frame_no);

      var caption;
      if (ph) {
        caption = buildCaption(
          esc(ph.title || ('Frame ' + pad(fno))),
          esc(ph.description || alt),
          'Frame ' + pad(ph.frame_no || fno) + (ph.location ? ' · ' + ph.location : '')
        );
      } else {
        caption = buildCaption(
          'Frame ' + pad(fno) + (scale !== 'column' ? ' · ' + scale.charAt(0).toUpperCase() + scale.slice(1) : ''),
          alt,
          scale === 'bleed' ? '' : '3:2'
        );
      }

      var fig = buildFigure(scale, [wrap], caption, false);
      // A 'plate' solves its own width from the frame's aspect (LOS7-2797), so
      // that a height-capped image and its caption stay the same width. Setting
      // only a max-height would leave the figcaption at the full figure width
      // and overhang the photograph on both sides, which is exactly the defect
      // LOS7-2793 fixed for duo.
      if (scale === 'plate' && ph && ph.width && ph.height) {
        fig.style.setProperty('--ar', (ph.width / ph.height).toFixed(4));
      }
      fig.classList.add('lc-fig');
      if (ph && ph.frame_no) fig.dataset.frameNo = String(ph.frame_no || fno);
      if (ph && ph.print_handle) fig.dataset.printHandle = ph.print_handle;

      if (!ph || !ph.camera) {
        fig.dataset.noExif = 'true';
      } else {
        var exifDl = buildExifPanel(ph);
        if (exifDl) fig.appendChild(exifDl);
      }

      // Inline credit beneath the figure, matching the figure.lc-news
      // figcaption credit treatment (readable sentence case, not the
      // mono-uppercase .lc-archive link chip). ADR-0008 / Blocker B2 + H1.
      var creditInfo = creditsEnabled ? creditTextFor(ph) : null;
      if (creditInfo && creditInfo.text) {
        var credit = el('span', { class: 'lc-fig-credit' });
        if (creditInfo.url) {
          credit.innerHTML = '<a href="' + esc(creditInfo.url) + '" rel="noopener" target="_blank">' + esc(creditInfo.text) + '</a>';
        } else {
          credit.textContent = creditInfo.text;
        }
        fig.appendChild(credit);
      }

      root.appendChild(fig);
      lastEmittedFigure = fig;
    }

    // Orientation of the frame that WILL be consumed next, read from
    // lco.photographs (every entry carries width and height). The source <img>
    // tags in body_html carry no width/height attributes and naturalWidth is 0
    // until the image loads, so the metafield is the only build-time source of
    // truth. offset is 1-based from the current bodyImgIndex, matching the
    // ++bodyImgIndex the builders perform.
    function orientAt(offset) {
      var ph = photographs[String(bodyImgIndex + offset)];
      if (!ph || !ph.width || !ph.height) return '?';
      var r = ph.width / ph.height;
      return r > 1.05 ? 'L' : (r < 0.95 ? 'P' : 'S');
    }

    function processGrid(div) {
      flushProse();
      var imgs = Array.prototype.slice.call(div.querySelectorAll('img'));
      if (imgs.length === 2) return processPairImpl(imgs);
      if (imgs.length === 3) {
        // A portrait beside two landscapes cannot be made to work three-across:
        // equal column widths make the portrait tiny or crop it, and equal
        // heights make it many times wider than its neighbours. Lay it out
        // instead as the portrait alongside the two landscapes STACKED, with
        // the stack's outer edges flush to the portrait's.
        var o = orientAt(1) + orientAt(2) + orientAt(3);
        var pCount = o.split('P').length - 1;
        var lCount = o.split('L').length - 1;
        if (pCount === 1 && lCount === 2) return processDuoImpl(imgs, o.indexOf('P'));
        return processTriImpl(imgs);
      }
      imgs.forEach(processImg);
    }

    // Portrait + two stacked landscapes. The two columns are given the SAME
    // explicit height (--duo-h, sized by sizeDuo below), so the top edges and
    // the bottom edges align exactly by construction rather than by tuning
    // column widths against the images' aspect ratios. Captions sit below each
    // column: a caption between the stacked pair would push the lower image
    // down and break the bottom alignment, so the stack's two captions follow
    // its images and carry their frame numbers to keep the association.
    function processDuoImpl(imgs, portraitIdx) {
      var wraps = [];
      var caps = [];
      var i, fno;
      for (i = 0; i < 3; i++) {
        fno = ++frameNo;
        bodyImgIndex++;
        wraps.push(imgWrap(imgs[i], fno, portraitIdx === i ? 'column' : 'pair', bodyImgIndex === 1, photographs[String(bodyImgIndex)] || null));
        caps.push(buildCaption('Frame ' + pad(fno), imgs[i].getAttribute('alt') || '', ''));
      }
      var fig = el('figure', { class: 'fig duo' });
      var grid = el('div', { class: 'duo-grid' });

      var tall = el('div', { class: 'duo-col duo-col--tall' });
      tall.appendChild(wraps[portraitIdx]);
      tall.appendChild(caps[portraitIdx]);

      var stack = el('div', { class: 'duo-col duo-col--stack' });
      var stackImgs = el('div', { class: 'duo-imgs' });
      var order = [];
      for (i = 0; i < 3; i++) { if (i !== portraitIdx) order.push(i); }
      order.forEach(function (k) { stackImgs.appendChild(wraps[k]); });
      stack.appendChild(stackImgs);
      order.forEach(function (k) { stack.appendChild(caps[k]); });

      // Keep the portrait on the side it was authored on.
      if (portraitIdx === 2) { grid.appendChild(stack); grid.appendChild(tall); }
      else { grid.appendChild(tall); grid.appendChild(stack); }

      fig.appendChild(grid);
      // The aspects are known now, from the same metafield that chose this
      // layout, so the height can be solved rather than guessed.
      fig.setAttribute('data-duo-p', duoAspect(portraitIdx, 0));
      fig.setAttribute('data-duo-l', duoAspect(portraitIdx, 1));
      root.appendChild(fig);
      lastEmittedFigure = fig;
      duoFigures.push(fig);
    }

    // Aspect (w/h) of the portrait (which=0) or the widest landscape (which=1)
    // among the three frames just consumed.
    function duoAspect(portraitIdx, which) {
      var base = bodyImgIndex - 3;
      var best = 0;
      for (var i = 0; i < 3; i++) {
        var ph = photographs[String(base + i + 1)];
        if (!ph || !ph.width || !ph.height) continue;
        var r = ph.width / ph.height;
        if (which === 0 && i === portraitIdx) return r.toFixed(4);
        if (which === 1 && i !== portraitIdx && r > best) best = r;
      }
      return (best || 1.5).toFixed(4);
    }

    // One grid cell of a pair/tri: the image wrap plus that image's OWN
    // caption. Previously pair/tri emitted a single shared figcaption built
    // from the first non-empty alt, which discarded every other image's alt
    // text outright. A nested <figure> is valid HTML and lets each image carry
    // its own <figcaption>.
    function buildGridItem(cls, wrap, label, alt) {
      var item = el('figure', { class: cls });
      item.appendChild(wrap);
      item.appendChild(buildCaption(label, alt || '', ''));
      return item;
    }

    // A pair or tri set directly under a plate takes the plate's own width
    // (Ikeshima office frames, 2026-09-26: Ocean Outlook above, Shelf and
    // Window beside Corner Office below, "the two together the same width as
    // the one above"). The plate is held under the group width by its 92vh
    // ceiling on any viewport under about 912px tall (a 3:2 plate stops 17px
    // short at 1440x900, 170px at 1280x720) while the group beneath kept the
    // full width. Only when nothing stands between them: flushProse() has
    // already run, so prose in between is the last child here, not the plate.
    function matchPlateAbove(fig) {
      var prev = root.lastElementChild;
      if (!prev || !prev.classList.contains('plate')) return;
      var ar = prev.style.getPropertyValue('--ar');
      if (!ar) return;
      fig.classList.add('lc-under-plate');
      fig.style.setProperty('--plate-ar', ar);
    }

    function processPairImpl(imgs) {
      var items = [];
      for (var i = 0; i < 2; i++) {
        var fno = ++frameNo;
        bodyImgIndex++;
        items.push(buildGridItem(
          'pair-item',
          imgWrap(imgs[i], fno, 'pair', bodyImgIndex === 1, photographs[String(bodyImgIndex)] || null),
          'Frame ' + pad(fno),
          imgs[i].getAttribute('alt')
        ));
      }
      var fig = buildFigure('pair', items, null, true);
      matchPlateAbove(fig);
      root.appendChild(fig);
      lastEmittedFigure = fig;
    }

    function processTriImpl(imgs) {
      var fig = el('figure', { class: 'fig tri' });
      var triGrid = el('div', { class: 'tri-grid' });
      for (var i = 0; i < 3; i++) {
        var fno = ++frameNo;
        bodyImgIndex++;
        triGrid.appendChild(buildGridItem(
          'tri-item',
          imgWrap(imgs[i], fno, 'tri', bodyImgIndex === 1, photographs[String(bodyImgIndex)] || null),
          'Frame ' + pad(fno),
          imgs[i].getAttribute('alt')
        ));
      }
      fig.appendChild(triGrid);
      matchPlateAbove(fig);
      root.appendChild(fig);
      lastEmittedFigure = fig;
    }

    function processIframe(iframe) {
      flushProse();
      var fig = el('figure', { class: 'fig embed' });
      var frame = el('div', { class: 'frame' });
      var clean = document.createElement('iframe');
      clean.src = iframe.getAttribute('src') || '';
      clean.setAttribute('allowfullscreen', '');
      clean.setAttribute('frameborder', '0');
      clean.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture');
      frame.appendChild(clean);
      fig.appendChild(frame);
      var src = iframe.getAttribute('src') || '';
      var label = 'REFERENCE · EMBED'; var desc = '';
      if (src.indexOf('youtube') > -1 || src.indexOf('youtu.be') > -1) { label = 'REFERENCE · VIDEO'; desc = 'YouTube embed.'; }
      else if (src.indexOf('vimeo') > -1) { label = 'REFERENCE · VIDEO'; desc = 'Vimeo embed.'; }
      else if (src.indexOf('google.com/maps') > -1) { label = 'REFERENCE · MAP'; desc = 'Google Maps embed.'; }
      fig.appendChild(buildCaption(label, desc, '16:9 · EMBED'));
      root.appendChild(fig);
      lastEmittedFigure = fig;
    }

    function processH3(h3) {
      flushProse();
      sectionNo++;
      var titleText = h3.textContent.trim();
      var firstFrame = sectionFrameStart;
      var sectionMeta = h3.getAttribute('data-section-meta');
      var h2 = el('h2', { class: 'h2-mark lc-sec-hd' });
      // Carried through the rebuild for the archive-band pass below.
      var surface = h3.getAttribute('data-surface');
      if (surface) h2.setAttribute('data-surface', surface);
      var eyebrow = el('div', { class: 'lc-sec-hd__eyebrow' });
      eyebrow.appendChild(el('span', { class: 'lc-sec-hd__num' }, pad(sectionNo)));
      // Scrollytelling articles carry no frame range (LOS7-3032). Their
      // scrolly frames are not numbered, so the ranges counted only the inline
      // figures and each one landed on the chapter before ("Over the gate"
      // read 04-16, which are The island's). Field notes keep theirs.
      if (sectionMeta) {
        eyebrow.appendChild(el('span', { class: 'lc-sec-hd__sep', 'aria-hidden': 'true' }, '\u00b7'));
        eyebrow.appendChild(el('span', { class: 'lc-sec-hd__label' }, sectionMeta));
      } else if (!isScrollyArticle) {
        eyebrow.appendChild(el('span', { class: 'lc-sec-hd__sep', 'aria-hidden': 'true' }, '\u00b7'));
        eyebrow.appendChild(el('span', { class: 'lc-sec-hd__label frame', 'data-frame-from': pad(firstFrame) }, 'Frames ' + pad(firstFrame) + '\u2013'));
      }
      eyebrow.appendChild(el('span', { class: 'lc-sec-hd__rule', 'aria-hidden': 'true' }));
      h2.appendChild(eyebrow);
      h2.appendChild(el('span', { class: 'lc-sec-hd__wordmark ttl' }, titleText));
      root.appendChild(h2);
      sectionFrameStart = frameNo + 1;
      lastEmittedFigure = null;
    }

    function processH4(h4) {
      flushProse();
      var text = h4.textContent.trim();
      var h3 = el('h3', { class: 'h3-sub' }, text);
      root.appendChild(h3);
      lastEmittedFigure = null;
    }

    function processBlockquote(bq) {
      flushProse();
      var pull = el('blockquote', { class: 'pull' }, bq.innerHTML);
      root.appendChild(pull);
      lastEmittedFigure = null;
    }

    // ----- Exchange block (LOS7-2659) ------------------------------------
    // Author convention: a <p> whose lines are spoken exchanges separated
    // by <br>, no speaker labels. Becomes <div class="lc-exchange"> with one
    // <p> per line. Detected when either
    //   (a) the first line opens with ">>" (the marker is stripped; always
    //       wins, so an exchange can follow ordinary prose), or
    //   (b) the paragraph has two or more <br>-separated lines, each 120
    //       characters of text or fewer, and it directly follows a chapter
    //       h2 or another exchange with no prose between.
    // Blank lines (a doubled <br>) are dropped. Wangi's body has no <br>
    // paragraphs at all, so (b) never fires there (checked 2026-09-05).
    var EXCHANGE_MAX = 120;
    function exchangeLines(p) {
      var html = p.innerHTML || '';
      var hasBr = /<br\s*\/?>/i.test(html);
      var marked = /^\s*(?:&gt;&gt;|>>)/.test(html);
      if (!hasBr && !marked) return null;
      var lines = html.split(/<br\s*\/?>/i).map(function (seg) {
        return seg.replace(/^(?:\s|&nbsp;)+|(?:\s|&nbsp;)+$/g, '');
      }).filter(function (seg) {
        return seg && seg.replace(/<[^>]*>/g, '').trim();
      });
      if (!lines.length) return null;
      if (marked) {
        lines[0] = lines[0].replace(/^(?:&gt;&gt;|>>)\s*/, '');
        if (!lines[0].replace(/<[^>]*>/g, '').trim()) lines.shift();
        return lines.length ? lines : null;
      }
      if (lines.length < 2) return null;
      if (pendingProse.length) return null;
      var last = root.lastElementChild;
      if (!last || !last.classList ||
          !(last.classList.contains('h2-mark') || last.classList.contains('lc-exchange'))) return null;
      for (var i = 0; i < lines.length; i++) {
        var probe = el('span', {}, lines[i]);
        if ((probe.textContent || '').trim().length > EXCHANGE_MAX) return null;
      }
      return lines;
    }

    // Speaker mapping (LOS7-3032). Opt-in: class tokens lc-me-N on the
    // source <p> mark line N (1-based) as the author's. A block with any
    // marker is .lc-exchange--mapped, where marked lines step in and drop to
    // graphite and every other line is ink, whatever its position. Blocks
    // with no marker keep the positional rule (the reply steps in).
    function speakerMarks(src) {
      var marks = {};
      var any = false;
      if (src && src.classList) {
        Array.prototype.forEach.call(src.classList, function (c) {
          var m = /^lc-me-(\d+)$/.exec(c);
          if (m) { marks[parseInt(m[1], 10)] = true; any = true; }
        });
      }
      return any ? marks : null;
    }

    function wordCount(text) {
      var t = (text || '').trim();
      return t ? t.split(/\s+/).length : 0;
    }

    // A stage beat ("He laughed.") folded into the preceding exchange. The
    // next exchange then joins the same block instead of opening its own.
    var exchangeToExtend = null;

    function processExchange(lines, src) {
      flushProse();
      var block = null;
      if (exchangeToExtend && root.lastElementChild === exchangeToExtend) block = exchangeToExtend;
      exchangeToExtend = null;
      if (!block) {
        block = el('div', { class: 'lc-exchange' });
        root.appendChild(block);
      }
      var marks = speakerMarks(src);
      if (marks) block.classList.add('lc-exchange--mapped');
      lines.forEach(function (line, i) {
        var lp = el('p', {}, line);
        if (marks && marks[i + 1]) lp.classList.add('is-me');
        // A long speech steps down in size (keeps the hanging quote).
        if (wordCount(lp.textContent) > 25) lp.classList.add('is-long');
        block.appendChild(lp);
      });
      lastEmittedFigure = null;
    }

    // Would this source node become an exchange? Only the stateless part of
    // exchangeLines: the >> marker, or two or more short <br> lines.
    function looksLikeExchange(n) {
      if (!n || n.nodeType !== 1 || (n.tagName || '').toLowerCase() !== 'p') return false;
      var html = n.innerHTML || '';
      if (/^\s*(?:&gt;&gt;|>>)/.test(html)) return true;
      var lines = html.split(/<br\s*\/?>/i).filter(function (seg) {
        return seg.replace(/<[^>]*>/g, '').trim();
      });
      return lines.length >= 2 && lines.every(function (seg) {
        return seg.replace(/<[^>]*>/g, '').trim().length <= EXCHANGE_MAX;
      });
    }
    var BEAT_MAX_WORDS = 8;

    function processP(p) {
      var nestedImg = p.querySelector(':scope > img');
      var nestedIframe = p.querySelector(':scope > iframe');
      if (nestedImg && p.textContent.trim() === '') return processImg(nestedImg);
      if (nestedIframe && p.textContent.trim() === '') return processIframe(nestedIframe);
      if (lastEmittedFigure && isCaptionParagraph(p)) {
        attachCaptionToFigure(p, lastEmittedFigure);
        return;
      }
      // Prints marker as a paragraph (LOS7-2659): the editor may not keep an
      // HTML comment, so <p>[[prints]]</p> is the equivalent of <!-- prints -->.
      if ((p.textContent || '').trim() === '[[prints]]') {
        flushProse();
        root.appendChild(document.createComment(' prints '));
        lastEmittedFigure = null;
        return;
      }
      var exchange = exchangeLines(p);
      if (exchange) return processExchange(exchange, p);
      // Stage beat (LOS7-3032, scrolly only): a paragraph of eight words or
      // fewer with an exchange directly before AND after it sits inside the
      // exchange block as a quiet stage line, rather than as an orphan
      // between two display-size blocks.
      var prevBlock = root.lastElementChild;
      if (isScrollyArticle && !pendingProse.length && prevBlock &&
          prevBlock.classList && prevBlock.classList.contains('lc-exchange') &&
          wordCount(p.textContent) <= BEAT_MAX_WORDS && looksLikeExchange(nextSource)) {
        var beat = el('p', { class: 'lc-exchange__beat' }, p.innerHTML);
        prevBlock.appendChild(beat);
        exchangeToExtend = prevBlock;
        lastEmittedFigure = null;
        return;
      }
      var clone = p.cloneNode(true);
      // The phone-screen line (.lc-screen) is its own device, never the lede,
      // and neither is the setup line right before it: at 22px that sentence
      // outranked the line it sets up. Either case ends lede eligibility, so
      // the lede never lands on a later paragraph below the box (LOS7-3032).
      var isScreen = clone.classList.contains('lc-screen');
      var beforeScreen = !!(nextSource && nextSource.nodeType === 1 &&
        nextSource.classList && nextSource.classList.contains('lc-screen'));
      if (firstPara && (isScreen || beforeScreen)) firstPara = false;
      if (firstPara && clone.textContent.trim().length > 40) {
        clone.classList.add('lede');
        firstPara = false;
      }
      pendingProse.push(clone);
    }

    function processComment(node) {
      // <!-- lc:wall --> is handled in Pass 3, <!-- prints --> in Pass 11.
      // Flush pending prose first so the marker keeps its authored position
      // (LOS7-2659): without this a comment placed after a run of paragraphs
      // landed BEFORE their prose-block, and the prints interstitial then sat
      // above the chapter's scrolly block instead of after it.
      flushProse();
      root.appendChild(node.cloneNode(false));
    }

    children.forEach(function (n, idx) {
      nextSource = children[idx + 1] || null;
      if (n.nodeType === 8) return processComment(n);
      if (n.nodeType === 3) {
        var t = n.textContent.trim();
        if (t) processP(el('p', {}, t));
        return;
      }
      var tag = n.tagName ? n.tagName.toLowerCase() : '';
      switch (tag) {
        case 'p':          return processP(n);
        case 'h3':         return processH3(n);
        case 'h2':         return processH3(n);
        case 'h4':
        case 'h5':         return processH4(n);
        case 'blockquote': return processBlockquote(n);
        case 'img':        return processImg(n);
        case 'iframe':     return processIframe(n);
        case 'figure':
          flushProse(); root.appendChild(n.cloneNode(true)); return;
        case 'div':
          if (n.classList && (n.classList.contains('uk-grid') || n.classList.contains('lc-pair'))) {
            return processGrid(n);
          }
          if (n.textContent.trim()) {
            var p2 = el('p', {}, n.innerHTML);
            return processP(p2);
          }
          return;
        case 'ul':
        case 'ol':
          flushProse();
          var listBlock = el('div', { class: 'prose-block' });
          listBlock.appendChild(n.cloneNode(true));
          root.appendChild(listBlock);
          return;
        default:
          if (n.textContent && n.textContent.trim()) {
            var p3 = el('p', {}, n.outerHTML);
            return processP(p3);
          }
      }
    });
    flushProse();

    keepers.forEach(function (k) { root.appendChild(k); });

    // ----- Pass 2: patch § frame ranges -----------------------------------
    var sections = root.querySelectorAll('h2.h2-mark');
    sections.forEach(function (h2, i) {
      var frameSpan = h2.querySelector('.frame');
      if (!frameSpan) return;
      var from = frameSpan.getAttribute('data-frame-from');
      if (!from) return;
      var to;
      var nextH2 = sections[i + 1];
      // A next section built from a data-section-meta h3 carries no .frame
      // span - guard it like the current h2 above, else the whole
      // postprocess chain aborts on the thrown TypeError.
      var nextSpan = nextH2 ? nextH2.querySelector('.frame') : null;
      if (nextSpan && nextSpan.getAttribute('data-frame-from')) {
        to = pad(parseInt(nextSpan.getAttribute('data-frame-from'), 10) - 1);
      } else {
        to = pad(frameNo);
      }
      // `to` is the next section's start minus one, so a section that holds no
      // body images shares the next one's start and lands on from-1: an
      // inverted range like "Frames 04-03". Such a section has no frames of
      // its own, so it gets no range label at all. A section holding exactly
      // one frame reads "Frame 07", not "Frames 07-07".
      var fromNum = parseInt(from, 10);
      var toNum = parseInt(to, 10);
      if (!(toNum >= fromNum)) {
        // Take the preceding "·" with it, or the eyebrow reads "01 ·" with
        // nothing after the separator.
        var sep = frameSpan.previousElementSibling;
        if (sep && sep.classList.contains('lc-sec-hd__sep')) sep.remove();
        frameSpan.remove();
        return;
      }
      frameSpan.textContent =
        toNum === fromNum ? 'Frame ' + from : 'Frames ' + from + '\u2013' + to;
    });

    // ----- Pass 3: expand <!-- lc:wall --> comment nodes ------------------
    var wallTemplate = document.getElementById('lc-wall-source');
    if (wallTemplate) {
      var it = document.createNodeIterator(root, NodeFilter.SHOW_COMMENT);
      var commentNode;
      while ((commentNode = it.nextNode())) {
        if (/lc:wall/i.test(commentNode.nodeValue)) {
          var clone = wallTemplate.content.cloneNode(true);
          commentNode.parentNode.replaceChild(clone, commentNode);
          break;
        }
      }
    }

    // ----- Pass 3.4: size the portrait + stacked-pair figures -------------
    // Both columns are given the same explicit height, so alignment is exact.
    // Solve that height from the figure's own width and the frames' aspects:
    //   W = H*p + g + ((H - g) / 2) * L
    // where p is the portrait's aspect, L the wider landscape's, and g the gap.
    // Rearranged:  H = (W - g * (1 - L / 2)) / (p + L / 2)
    // Then cap it so a tall figure cannot exceed the viewport, which is the
    // whole reason a portrait was a problem in the first place.
    function sizeDuo() {
      for (var i = 0; i < duoFigures.length; i++) {
        var fig = duoFigures[i];
        var grid = fig.querySelector('.duo-grid');
        if (!grid) continue;
        var p = parseFloat(fig.getAttribute('data-duo-p')) || 0.6667;
        var L = parseFloat(fig.getAttribute('data-duo-l')) || 1.5;
        var g = parseFloat(window.getComputedStyle(grid).columnGap);
        if (!(g >= 0)) g = 24;
        var W = fig.clientWidth;
        if (!(W > 0)) continue;
        var H = (W - g * (1 - L / 2)) / (p + L / 2);
        var vh = window.innerHeight || document.documentElement.clientHeight;
        var cap = Math.round(vh * 0.78);
        if (H > cap) H = cap;
        if (H < 160) H = 160;
        H = Math.round(H);
        fig.style.setProperty('--duo-h', H + 'px');
        // Publish each column's width as well. The images are sized by HEIGHT
        // (width:auto), so their natural widths are H*p and ((H-g)/2)*L. Without
        // these, .duo-col is a flex item with no width and its figcaption -
        // whose max-content width is a whole sentence - decides the column,
        // which left every caption wider than its own photograph (LOS7-2793).
        // The three terms are the same ones H was solved from, so they still
        // sum to W (or less, when the viewport cap above bites, and the row
        // just centres).
        // Derived from the SAME rounded H the images are given, and left
        // fractional, so the column is the width the browser actually paints
        // the image at rather than a pixel off it.
        fig.style.setProperty('--duo-w-p', (H * p).toFixed(2) + 'px');
        fig.style.setProperty('--duo-w-l', ((H - g) / 2 * L).toFixed(2) + 'px');
      }
    }

    if (duoFigures.length) {
      sizeDuo();
      var duoTimer = null;
      window.addEventListener('resize', function () {
        if (duoTimer) window.clearTimeout(duoTimer);
        duoTimer = window.setTimeout(function () { duoTimer = null; sizeDuo(); }, 120);
      }, { passive: true });
      // The first solve can run before Geist has loaded. --col-w is 72ch, so
      // in the fallback font the figure is about 140px narrower, and the
      // height solved from that width was kept after the font landed: 784px
      // where 858 belongs on Ikeshima at 1440x1100, on some loads and not
      // others. Re-solve whenever a duo's width changes, whatever changed it.
      // Only the width is compared, so the height this writes cannot loop.
      if (window.ResizeObserver) {
        var duoWidths = [];
        var duoRo = new ResizeObserver(function (entries) {
          var changed = false;
          entries.forEach(function (e) {
            var i = duoFigures.indexOf(e.target);
            var w = Math.round(e.contentRect.width);
            if (i >= 0 && duoWidths[i] !== w) { duoWidths[i] = w; changed = true; }
          });
          if (changed) sizeDuo();
        });
        duoFigures.forEach(function (f) { duoRo.observe(f); });
      }
    }

    // ----- Pass 3.5: update hero frame counter with computed total --------
    var heroFno = document.querySelector('.art-hero .frame-no');
    if (heroFno) {
      var preDeclared = heroFno.getAttribute('data-frame-total');
      if (!preDeclared || preDeclared === '') {
        if (frameNo > 1) heroFno.textContent = 'Frame 01 / ' + pad(frameNo);
        else heroFno.style.display = 'none';
      }
    }

    // ----- Pass 7: drop-cap enforcement ------------------------------------
    // processP() adds .lede to the first long paragraph during rebuild.
    // This ensures it also applies on pure-text articles where the first
    // paragraph is not followed by an image.
    if (document.body.getAttribute('data-dropcap') === 'true') {
      var firstParagraph = root.querySelector('.prose-block > p:first-child');
      var screenNext = firstParagraph && firstParagraph.nextElementSibling;
      if (firstParagraph && !firstParagraph.classList.contains('lede') &&
          !firstParagraph.classList.contains('lc-screen') &&
          !(screenNext && screenNext.classList.contains('lc-screen'))) {
        var textLen = (firstParagraph.textContent || '').trim().length;
        if (textLen > 40) firstParagraph.classList.add('lede');
      }
    }

    // ----- Pass 10: move article-images figures into body -----------------
    // Finds figures in lc-article-images-section (rendered after main-article)
    // and moves each into #articleBody at its section_anchor position. Must
    // run before scrolly init so the fixed-image layer measures correct
    // body height with figures in place.
    var imgSection = document.querySelector('.lc-article-images-section, .lc-article-images');
    if (imgSection) {
      function findH2ForAnchor(anchor) {
        var found = null;
        root.querySelectorAll('h2.h2-mark').forEach(function (h2) {
          var ttl = h2.querySelector('.ttl');
          if (!ttl) return;
          var slug = ttl.textContent.trim().toLowerCase()
            .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
          if (slug === anchor || h2.id === anchor) found = h2;
        });
        return found;
      }
      function fallbackInsert(fig) {
        var colophon = root.querySelector('.colophon, .lc-colophon, .read-next, .lc-next');
        if (colophon) root.insertBefore(fig, colophon);
        else root.appendChild(fig);
      }
      var outsideFigs = Array.prototype.slice.call(
        imgSection.querySelectorAll('figure[data-section-anchor]')
      );
      outsideFigs.forEach(function (fig) {
        var anchor = fig.getAttribute('data-section-anchor') || '';
        if (!anchor || anchor === 'opener') {
          if (hasScrolly) {
            fallbackInsert(fig);
          } else {
            var firstH2 = root.querySelector('h2.h2-mark');
            if (firstH2) root.insertBefore(fig, firstH2);
            else fallbackInsert(fig);
          }
          return;
        }
        var targetH2 = findH2ForAnchor(anchor);
        if (targetH2) {
          if (hasScrolly) root.insertBefore(fig, targetH2.nextSibling);
          else root.insertBefore(fig, targetH2);
        } else {
          fallbackInsert(fig);
        }
      });
    }

    // ----- Pass 11: prints interstitial (LOS7-2659) ----------------------
    // sections/main-article.liquid renders article-series-prints a second
    // time, hidden, as .lc-article-prints--interstitial[data-prints-interstitial]
    // outside #articleBody. Move it onto the body's <!-- prints --> comment
    // (Pass 1 keeps comment nodes, and turns a <p>[[prints]]</p> into one).
    // A marker with no block is simply removed; a block with no marker
    // stays hidden where it is.
    try {
      var printsBlock = document.querySelector('[data-prints-interstitial]');
      var printsMarker = null;
      var printsIt = document.createNodeIterator(root, NodeFilter.SHOW_COMMENT);
      var printsNode;
      while ((printsNode = printsIt.nextNode())) {
        if (/^\s*prints\s*$/i.test(printsNode.nodeValue || '')) { printsMarker = printsNode; break; }
      }
      if (printsMarker && printsMarker.parentNode) {
        if (printsBlock) {
          printsMarker.parentNode.replaceChild(printsBlock, printsMarker);
          printsBlock.removeAttribute('hidden');
        } else {
          printsMarker.parentNode.removeChild(printsMarker);
        }
      }
    } catch (_e) {}

    // LOS7-535: the article-body two-tone statements (.lc-leadin / .lc-sec-quote)
    // take the canonical ochre terminal dot. The closing period lives in the
    // prose, so strip it from the final clause and append the ochre period span
    // (styled globally by assets/lc-two-tone.css). Idempotent.
    try {
      root.querySelectorAll('figure.lc-leadin .lc-leadin__cont, figure.lc-sec-quote .lc-sec-quote__line').forEach(function (line) {
        if (line.querySelector('.lc-collection-title__period')) return;
        var node = line;
        while (node.lastChild) node = node.lastChild;
        if (!node || node.nodeType !== 3) return;
        var t = node.nodeValue.replace(/\s+$/, '');
        var m = t.match(/[.?!]$/);
        if (!m) return;
        node.nodeValue = t.slice(0, -1);
        line.appendChild(el('span', { class: 'lc-collection-title__period', 'aria-hidden': 'true' }, m[0]));
      });
    } catch (_e) {}

    // ----- Pass 12: archive band ------------------------------------------
    // Journal and survey scans are printed on pure white, and on the cream
    // paper they read as pasted rectangles. A chapter whose h2 carries
    // data-surface="archive" is wrapped, from that h2 up to (not including)
    // the next h2, in a full-bleed .lc-archive-band on --lc-archive. The
    // band also stops at a closing module (the keepers above, such as the
    // contact sheet, and the prints interstitial), which are bands of their
    // own and are laid out as direct children of the body. Last of the
    // passes, because Pass 10 and Pass 11 still insert beside root-level h2s.
    // The h2 element itself moves, so its id and the chapter rail, which
    // finds h2s anywhere under root, are untouched. Two archive chapters in a
    // row share one band. The body is flagged so the contact sheet's Archive
    // run takes the same white.
    try {
      root.querySelectorAll(':scope > h2[data-surface="archive"]').forEach(function (h2) {
        var prev = h2.previousElementSibling;
        var band = prev && prev.classList.contains('lc-archive-band') ? prev : null;
        if (!band) {
          band = el('div', { class: 'lc-archive-band' });
          root.insertBefore(band, h2);
        }
        var node = h2;
        while (node) {
          var next = node.nextSibling;
          band.appendChild(node);
          node = next;
          if (node && node.nodeType === 1 && (node.tagName.toLowerCase() === 'h2' ||
              isKeeper(node) || node.classList.contains('lc-article-prints'))) break;
        }
        root.classList.add('lc-has-archive-band');
      });
    } catch (_e) {}

    root.setAttribute('data-lc-processed', '1');

    } catch (err) {
      // Any thrown error inside a Pass below sets data-lc-processed=0 so a
      // subsequent reload can try again, then logs to the console for
      // observability. The finally block guarantees fireReady so dependent
      // modules don't hang.
      if (root) root.setAttribute('data-lc-processed', '0');
      try { console.error('[lc-article-postprocess] failed:', err); } catch (_e) {}
    } finally {
      // Signal dependent modules: body is rebuilt, figures are placed,
      // chapter h2s have stable ids and text — safe to wire TOC, lightbox,
      // pull quotes, contact sheet, and scrolly.
      safeFireReady();
    }
  });
})();
